/**
 * 炼丹系统引擎（经验制）
 */

import type { PlayerState } from './Player';
import type { Recipe } from '../data/recipes';
import {
  RECIPES,
  BASE_ALCHEMY_RATE,
  SKILL_BONUS_PER_LEVEL,
  COMPLETE_RECIPE_BONUS,
  INCOMPLETE_RECIPE_PENALTY,
} from '../data/recipes';
import { ITEMS } from '../data/items';

export interface AlchemyResult {
  success: boolean;
  message: string;
  expGained: number;
  leveledUp: boolean;
  itemGained?: { id: string; count: number };
}

/** 经验 → 等级：累计经验达到 expToLevel(n) 即为 n 级 */
function expToLevel(level: number): number {
  return Math.floor(5 * Math.pow(level, 1.6));
}

/** 根据经验值获取当前等级 */
export function getAlchemyLevel(exp: number): number {
  let lv = 0;
  while (expToLevel(lv + 1) <= exp) lv++;
  return lv;
}

/** 获取当前等级的经验进度 { current, needed, percent } */
export function getAlchemyProgress(exp: number): { level: number; current: number; needed: number; percent: number } {
  const level = getAlchemyLevel(exp);
  const prevCap = level > 0 ? expToLevel(level) : 0;
  const nextCap = expToLevel(level + 1);
  const current = exp - prevCap;
  const needed = nextCap - prevCap;
  return { level, current, needed, percent: needed > 0 ? Math.floor((current / needed) * 100) : 100 };
}

/** 计算炼丹成功率 */
export function calcAlchemyRate(player: PlayerState, recipe: Recipe): number {
  const exp = player.alchemyExp ?? 0;
  const level = getAlchemyLevel(exp);
  let rate = BASE_ALCHEMY_RATE + level * SKILL_BONUS_PER_LEVEL;
  rate += recipe.isComplete ? COMPLETE_RECIPE_BONUS : INCOMPLETE_RECIPE_PENALTY;
  if (player.identityId?.startsWith('sect_alchemy')) rate += 0.15;
  return Math.min(0.95, Math.max(0.05, rate));
}

export function canCraft(player: PlayerState, recipe: Recipe): boolean {
  return getAlchemyLevel(player.alchemyExp ?? 0) >= recipe.minSkill;
}

export function hasIngredients(player: PlayerState, recipe: Recipe): string[] {
  const missing: string[] = [];
  for (const ing of recipe.ingredients) {
    const count = player.inventory.filter((i) => i.id === ing.itemId).length;
    if (count < ing.count) missing.push(`${ing.itemName}(${count}/${ing.count})`);
  }
  return missing;
}

function consumeIngredients(player: PlayerState, recipe: Recipe): void {
  for (const ing of recipe.ingredients) {
    for (let i = 0; i < ing.count; i++) {
      const idx = player.inventory.findIndex((item) => item.id === ing.itemId);
      if (idx !== -1) player.inventory.splice(idx, 1);
    }
  }
}

export function performAlchemy(player: PlayerState, recipe: Recipe): AlchemyResult {
  const missing = hasIngredients(player, recipe);
  if (missing.length > 0) {
    return { success: false, message: `材料不足：${missing.join('、')}`, expGained: 0, leveledUp: false };
  }

  if (!canCraft(player, recipe)) {
    const lv = getAlchemyLevel(player.alchemyExp ?? 0);
    return { success: false, message: `炼丹熟练度不足！需要${recipe.minSkill}级，当前${lv}级`, expGained: 0, leveledUp: false };
  }

  consumeIngredients(player, recipe);

  const rate = calcAlchemyRate(player, recipe);
  const roll = Math.random();
  const oldLevel = getAlchemyLevel(player.alchemyExp ?? 0);

  // 经验：成功 +5，失败 +2
  const expGain = roll < rate ? 5 : 2;
  player.alchemyExp = (player.alchemyExp ?? 0) + expGain;
  const newLevel = getAlchemyLevel(player.alchemyExp);
  const leveledUp = newLevel > oldLevel;

  if (roll < rate) {
    const template = ITEMS.find((i) => i.id === recipe.resultItemId);
    if (template) {
      for (let i = 0; i < recipe.resultCount; i++) {
        player.inventory.push(JSON.parse(JSON.stringify(template)));
      }
    }
    const lvMsg = leveledUp ? ` ⬆️熟练度升至${newLevel}级！` : '';
    return {
      success: true,
      message: `🔥 炼丹成功！获得 ${template?.name ?? recipe.resultItemId}×${recipe.resultCount}！（成功率${(rate*100).toFixed(0)}%）${lvMsg}`,
      expGained: expGain,
      leveledUp,
      itemGained: { id: recipe.resultItemId, count: recipe.resultCount },
    };
  } else {
    const lvMsg = leveledUp ? ` ⬆️熟练度升至${newLevel}级！` : '';
    return {
      success: false,
      message: `💥 炼丹失败！材料化为灰烬...（成功率${(rate*100).toFixed(0)}%）${lvMsg}`,
      expGained: expGain,
      leveledUp,
    };
  }
}

export function tryCompleteRecipe(
  player: PlayerState, recipeId: string, extraItemId: string
): { completed: boolean; message: string } {
  const recipe = RECIPES.find((r) => r.id === recipeId);
  if (!recipe) return { completed: false, message: '丹方不存在' };
  if (recipe.isComplete) return { completed: false, message: '这个丹方已经完整了' };
  if (!recipe.hiddenIngredientIndexes) return { completed: false, message: '丹方配置错误' };

  for (const idx of recipe.hiddenIngredientIndexes) {
    if (recipe.ingredients[idx].itemId === extraItemId) {
      recipe.isComplete = true;
      recipe.hiddenIngredientIndexes = [];
      return { completed: true, message: `🎉 你发现了「${recipe.name}」的完整配方！需要 ${recipe.ingredients.map((i) => `${i.itemName}×${i.count}`).join('、')}` };
    }
  }

  const idx = player.inventory.findIndex((i) => i.id === extraItemId);
  if (idx !== -1) player.inventory.splice(idx, 1);
  return { completed: false, message: `尝试失败——材料已消耗，不是「${recipe.name}」需要的。` };
}
